import gsap from 'gsap';

export default class GallerySlider {
  constructor({ container, visibleSlides = 3 }) {
    this.container = document.querySelector(container);
    if (!this.container) return;

    this.visibleSlides = visibleSlides;

    this.viewport = this.container.querySelector('.gallery-viewport');
    this.track = this.container.querySelector('.gallery-track');
    this.prevBtn = this.container.querySelector('.gallery-arrow--prev');
    this.nextBtn = this.container.querySelector('.gallery-arrow--next');
    this.dotsWrapper = this.container.querySelector('.gallery-dots');

    if (!this.viewport || !this.track || !this.dotsWrapper) return;

    // Slides réellement présentes dans le HTML (avant clonage)
    this.realSlides = [...this.track.children];
    this.realCount = this.realSlides.length;
    if (!this.realCount) return;

    this._buildLoop();
    this._buildDots();
    this._bindEvents();

    // On démarre au premier vrai slide : grâce aux clones, la voisine de
    // gauche est déjà la dernière image (boucle visible dès le chargement).
    this.positionIndex = this.realCount;

    this._resizeObserver = new ResizeObserver(() => this._update(false));
    this._resizeObserver.observe(this.viewport);
  }

  // Ajoute un jeu de clones avant et après les vraies slides, pour que la
  // piste ait toujours du contenu visuel à afficher de chaque côté.
  _buildLoop() {
    const cloneList = () => this.realSlides.map((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      return clone;
    });

    const beforeFragment = document.createDocumentFragment();
    cloneList().forEach((clone) => beforeFragment.appendChild(clone));
    this.track.insertBefore(beforeFragment, this.track.firstChild);

    const afterFragment = document.createDocumentFragment();
    cloneList().forEach((clone) => afterFragment.appendChild(clone));
    this.track.appendChild(afterFragment);

    this.slides = [...this.track.children]; // clones + vraies + clones
  }

  _buildDots() {
    this.dotsWrapper.innerHTML = '';
    this.realSlides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'gallery-dot';
      dot.type = 'button';
      dot.dataset.index = String(i);
      dot.setAttribute('aria-label', `Aller à l'image ${i + 1}`);
      this.dotsWrapper.appendChild(dot);
    });
    this.dots = [...this.dotsWrapper.querySelectorAll('.gallery-dot')];
  }

  _bindEvents() {
    this.prevBtn.addEventListener('click', () => this._step(-1));
    this.nextBtn.addEventListener('click', () => this._step(1));

    this.track.addEventListener('click', (e) => {
      const slide = e.target.closest('.gallery-slide');
      if (!slide) return;
      const position = this.slides.indexOf(slide);
      position === this.positionIndex ? this._openLightbox() : this._goToPosition(position);
    });

    this.dotsWrapper.addEventListener('click', (e) => {
      const dot = e.target.closest('.gallery-dot');
      if (dot) this._goToReal(Number(dot.dataset.index));
    });

    this.viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this._step(-1);
      if (e.key === 'ArrowRight') this._step(1);
      if (e.key === 'Enter') this._openLightbox();
    });

    this._bindSwipe();
  }

  // Navigation au doigt sur mobile : un swipe horizontal net avance/recule
  // d'une image, sans gêner le scroll vertical normal de la page.
  _bindSwipe() {
    const threshold = 40; // px minimum pour valider un swipe
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.viewport.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    }, { passive: true });

    this.viewport.addEventListener('touchmove', (e) => {
      if (!tracking) return;
      const touch = e.touches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      // Geste clairement horizontal : on bloque le scroll vertical de la page
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        e.preventDefault();
      }
    }, { passive: false });

    this.viewport.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      if (Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY)) {
        this._step(deltaX < 0 ? 1 : -1);
      }
    });
  }

  _step(delta) {
    this._goToPosition(this.positionIndex + delta);
  }

  // Navigue vers une image réelle (ex: clic sur un point), en choisissant
  // l'occurrence (clone ou vraie) la plus proche pour un déplacement court.
  _goToReal(realIndex) {
    const N = this.realCount;
    const candidates = [realIndex, realIndex + N, realIndex + 2 * N];
    const best = candidates.reduce((a, b) =>
      Math.abs(b - this.positionIndex) < Math.abs(a - this.positionIndex) ? b : a
    );
    this._goToPosition(best);
  }

  _goToPosition(position) {
    this.positionIndex = position;
    this._update(true);
  }

  // Une fois l'animation terminée, si on a glissé dans une zone clonée,
  // on se replace instantanément sur la vraie slide équivalente (invisible
  // à l'œil puisque le clone est identique).
  _normalizePosition() {
    const N = this.realCount;
    if (this.positionIndex >= 2 * N) {
      this.positionIndex -= N;
      this._update(false);
    } else if (this.positionIndex < N) {
      this.positionIndex += N;
      this._update(false);
    }
  }

  _update(animate) {
    const slideWidth = this.viewport.offsetWidth / this.visibleSlides;
    const centerOffset = Math.floor(this.visibleSlides / 2);
    const x = -(this.positionIndex - centerOffset) * slideWidth;

    this.slides.forEach((slide, i) => {
      slide.style.width = `${slideWidth}px`;

      const distance = Math.abs(i - this.positionIndex);
      const isActive = distance === 0;
      const opacity = isActive ? 1 : distance === 1 ? 0.45 : 0.18;
      const scale = isActive ? 1 : 0.95;

      const vars = { opacity, scale, duration: 0.6, ease: 'power3.out' };
      animate ? gsap.to(slide, vars) : gsap.set(slide, vars);

      slide.classList.toggle('is-active', isActive);
    });

    if (animate) {
      gsap.to(this.track, {
        x,
        duration: 0.6,
        ease: 'power3.out',
        onComplete: () => this._normalizePosition()
      });
    } else {
      gsap.set(this.track, { x });
    }

    const realIndex = this.positionIndex % this.realCount;
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === realIndex));
  }

  _openLightbox() {
    const data = this.realSlides.map((slide) => {
      const img = slide.querySelector('img');
      return { src: img.src, alt: img.alt };
    });
    const index = this.positionIndex % this.realCount;

    document.dispatchEvent(new CustomEvent('gallery:open', {
      detail: { index, data }
    }));
  }
}